/**
 * NeuGreen Energy — Intelligent Energy Ecosystem Germany
 * 12-slide native corporate presentation.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Home as HomeIcon, Maximize2, X, Play, Pause, Globe, ChevronDown, Check } from "lucide-react";
import { SLIDES, UI, SUPPORTED, PALETTE as P } from "./slidesData";
import { SlideRenderer } from "./SlideRenderer";

const LANG_META = {
  en: { name: "English",    short: "EN", flag: "🇬🇧" },
  de: { name: "Deutsch",    short: "DE", flag: "🇩🇪" },
  pt: { name: "Português",  short: "PT", flag: "🇵🇹" },
};

function InlineLangSwitcher({ current, onChange }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  const meta = LANG_META[current] || LANG_META.en;
  return (
    <div ref={ref} className="relative" data-testid="neugreen-lang-switcher">
      <button onClick={() => setOpen((v) => !v)}
        data-testid="neugreen-lang-button"
        className="flex items-center gap-2 px-3 py-2 rounded-md transition-all border"
        style={{ borderColor: open ? `${P.green}55` : P.border, background: P.surface }}>
        <Globe size={14} style={{ color: P.green }} />
        <span className="text-[14px]">{meta.flag}</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: P.white }}>{meta.short}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} style={{ color: P.silver }} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute top-full right-0 mt-2 w-52 rounded-lg overflow-hidden z-50 shadow-2xl"
            style={{ background: "rgba(15,17,20,0.98)", border: `1px solid ${P.green}25`, backdropFilter: "blur(20px)" }}>
            <div className="p-2">
              {SUPPORTED.map((code) => {
                const m = LANG_META[code];
                const active = code === current;
                return (
                  <button key={code} onClick={() => { onChange(code); setOpen(false); }}
                    data-testid={`neugreen-lang-option-${code}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all"
                    style={{ background: active ? `${P.green}12` : "transparent", color: active ? P.green : P.ink }}>
                    <span className="text-lg">{m.flag}</span>
                    <div className="flex-1 text-left">
                      <div className="font-semibold text-[13px]">{m.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: P.silver }}>{m.short}</div>
                    </div>
                    {active && <Check size={14} style={{ color: P.green }} />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function NeuGreenPage() {
  const { i18n } = useTranslation();
  const raw = (i18n.language || "de").split("-")[0];
  const lang = SUPPORTED.includes(raw) ? raw : "de";  // German-first default
  const u = UI[lang];

  const [idx, setIdx] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [zoom, setZoom] = React.useState(false);

  const next = React.useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);
  const prev = React.useCallback(() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "Escape") setZoom(false);
      else if (e.key === "f" || e.key === "F") setZoom((z) => !z);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  React.useEffect(() => {
    if (!playing) return;
    const t = setTimeout(next, 8500);
    return () => clearTimeout(t);
  }, [playing, idx, next]);

  const slide = SLIDES[idx];
  const title = slide.i18n[lang]?.title || slide.i18n.en.title;

  return (
    <div className="min-h-screen relative overflow-hidden"
      style={{ background: P.graphite, color: P.ink, fontFamily: "'Inter', 'Poppins', system-ui, sans-serif" }}
      data-testid="neugreen-page">

      {/* Ambient German-engineering grid */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `linear-gradient(${P.silver}0a 1px, transparent 1px), linear-gradient(90deg, ${P.silver}0a 1px, transparent 1px)`,
        backgroundSize: "56px 56px",
      }} />
      <div className="fixed -top-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${P.blue}18 0%, transparent 65%)` }} />
      <div className="fixed -bottom-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${P.green}18 0%, transparent 65%)` }} />

      {/* Header */}
      <header className="relative z-20 border-b" style={{ borderColor: P.border, background: "rgba(15,17,20,0.85)", backdropFilter: "blur(12px)" }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6 px-6 py-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid="neugreen-home-link">
            <img
              src="/neugreen/logo-mark.png"
              alt="NeuGreen Energy"
              className="h-11 w-auto object-contain transition group-hover:scale-105"
              style={{ filter: `drop-shadow(0 0 12px ${P.green}55)` }}
            />
            <div className="leading-none">
              <div className="font-bold text-[15px] tracking-tight" style={{ color: P.white }}>NeuGreen Energy</div>
              <div className="text-[9px] uppercase tracking-[0.28em] mt-1" style={{ color: P.silver }}>{u.footer.split(" · ").slice(1, 2).join("")}</div>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-3 h-8">
            <img src="/neugreen/logo-bluesun.png" alt="Bluesun Solar" title="Bluesun Solar · Solar"
              className="h-6 w-auto object-contain px-1 rounded"
              style={{ background: "rgba(255,255,255,0.94)" }} />
            <span className="opacity-30" style={{ color: P.silver }}>·</span>
            <img src="/neugreen/logo-wattzing.png" alt="Wattzing New Energy" title="Wattzing · Storage"
              className="h-6 w-auto object-contain px-1 rounded"
              style={{ background: "rgba(255,255,255,0.94)" }} />
            <span className="opacity-30" style={{ color: P.silver }}>·</span>
            <img src="/neugreen/logo-beny.png" alt="BENY New Energy" title="BENY · Mobility"
              className="h-6 w-auto object-contain px-1 rounded"
              style={{ background: "rgba(255,255,255,0.94)" }} />
            <span className="opacity-30" style={{ color: P.silver }}>·</span>
            <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold" style={{ color: P.silver }}>Digital</span>
          </div>

          <div className="flex items-center gap-3">
            <InlineLangSwitcher current={lang} onChange={(code) => i18n.changeLanguage(code)} />
            <Link to="/" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] hover:text-white transition"
              style={{ color: P.silver }} data-testid="neugreen-back-link">
              <HomeIcon size={13} /> {u.back}
            </Link>
          </div>
        </div>
      </header>

      {/* Main area */}
      <main className="relative z-10 max-w-[1400px] mx-auto px-4 lg:px-8 pt-8 pb-10">
        {/* Slide meta */}
        <div className="flex items-center justify-between mb-5 gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.green }}>
              {u.slide} {String(idx + 1).padStart(2, "0")} / {SLIDES.length}
              <span className="ml-3 opacity-60">· {u.intro}</span>
            </div>
            <div className="font-semibold text-[15px] sm:text-[17px] mt-2 tracking-tight truncate" style={{ color: P.white }}>
              {title}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setPlaying((v) => !v)}
              data-testid="neugreen-play-toggle"
              aria-label={playing ? "Pause" : "Play"}
              className="w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-105"
              style={{ background: playing ? `${P.green}22` : `${P.green}0d`, border: `1px solid ${P.green}55`, color: P.green }}>
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button onClick={() => setZoom(true)}
              data-testid="neugreen-zoom"
              aria-label="Fullscreen"
              className="w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-105"
              style={{ background: `${P.green}0d`, border: `1px solid ${P.green}55`, color: P.green }}>
              <Maximize2 size={15} />
            </button>
          </div>
        </div>

        {/* Slide viewport */}
        <motion.div
          key={`${idx}-${lang}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative rounded-lg overflow-hidden"
          data-testid="neugreen-slide-viewport"
          style={{
            border: `1px solid ${P.border}`,
            boxShadow: `0 40px 100px -30px ${P.blue}22, 0 2px 0 ${P.silver}0d inset`,
            background: P.graphite,
          }}>
          <SlideRenderer slide={slide} lang={lang} />
        </motion.div>

        {/* Controls */}
        <div className="mt-6 flex items-center justify-between gap-4">
          <button onClick={prev} data-testid="neugreen-prev"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold transition hover:translate-x-[-2px]"
            style={{ border: `1px solid ${P.green}55`, background: `${P.green}0d`, color: P.green }}>
            <ArrowLeft size={13} /> {u.prev}
          </button>

          <div className="flex items-center gap-1.5 overflow-x-auto max-w-[60%] scrollbar-hide">
            {SLIDES.map((s, i) => (
              <button key={s.id} onClick={() => setIdx(i)}
                data-testid={`neugreen-dot-${i + 1}`}
                aria-label={`Go to slide ${i + 1}`}
                className="shrink-0 transition-all"
                style={{
                  width: i === idx ? 28 : 8,
                  height: 8,
                  borderRadius: 4,
                  background: i === idx ? P.green : `${P.green}44`,
                  boxShadow: i === idx ? `0 0 12px -2px ${P.green}` : "none",
                }} />
            ))}
          </div>

          <button onClick={next} data-testid="neugreen-next"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold transition hover:translate-x-[2px]"
            style={{ background: `linear-gradient(90deg, ${P.green} 0%, ${P.blue} 100%)`, color: P.graphite, boxShadow: `0 12px 32px -8px ${P.green}88` }}>
            {u.next} <ArrowRight size={13} />
          </button>
        </div>
      </main>

      {/* Fullscreen mode */}
      {zoom && (
        <div className="fixed inset-0 z-[9999] p-4 sm:p-10 overflow-auto"
          style={{ background: "rgba(15,17,20,0.98)", backdropFilter: "blur(20px)" }}
          data-testid="neugreen-lightbox">
          <button onClick={() => setZoom(false)}
            className="fixed top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center transition hover:scale-110 z-[10000]"
            style={{ background: `${P.green}22`, border: `1px solid ${P.green}55`, color: P.green }}
            aria-label="Close"
            data-testid="neugreen-lightbox-close">
            <X size={18} />
          </button>
          <div className="max-w-[1500px] mx-auto rounded-lg overflow-hidden"
            style={{ border: `1px solid ${P.border}`, background: P.graphite }}>
            <SlideRenderer slide={slide} lang={lang} />
          </div>
          <div className="max-w-[1500px] mx-auto mt-6 flex items-center justify-between gap-4">
            <button onClick={prev} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-semibold"
              style={{ border: `1px solid ${P.green}55`, background: `${P.green}0d`, color: P.green }}>
              <ArrowLeft size={13} /> {u.prev}
            </button>
            <span className="text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
              {u.slide} {String(idx + 1).padStart(2, "0")} / {SLIDES.length}
            </span>
            <button onClick={next} className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] font-bold"
              style={{ background: `linear-gradient(90deg, ${P.green} 0%, ${P.blue} 100%)`, color: P.graphite }}>
              {u.next} <ArrowRight size={13} />
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="relative z-10 border-t py-6 px-6" style={{ borderColor: P.border }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4 text-[10px] uppercase tracking-[0.24em]" style={{ color: P.silver }}>
          <div>© {new Date().getFullYear()} NeuGreen Energy · Germany</div>
          <div>{u.footer}</div>
          <Link to="/" className="hover:text-white transition">← HannoNexus</Link>
        </div>
      </footer>
    </div>
  );
}
